import React from "react";
import { useProductForm } from "./hooks/useProductForm";
import { Form, Formik, FieldArray } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Dialog,
  ListSubheader,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";

function ProductForm({ editMode = false, setOpen, currentProduct }) {
  const {
    initialValues,
    validateSchema,
    handleSubmit,
    handleDelete,
    handleDeleteClick,
    handleCloseDeleteDialog,
    deleteProductDialogOpen,
    setIngredientSearchTerm,
    ingredientSearchResults,
    organisations,
    preparedOptions,
    filter,
    setSearchOrganisationId,
    activeOrgId,
    activeOrganisationName,
  } = useProductForm({
    editMode,
    setOpen,
    currentProduct,
  });

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validateSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          isSubmitting,
          getFieldProps,
          values,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          dirty,
          isValid,
        }) => {
          const totalPercentage = values.ingredients.reduce(
            (sum, ingredient) => sum + (Number(ingredient.percent) || 0),
            0
          );

          return (
            <Form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                id="name"
                label="Product Name"
                variant="outlined"
                {...getFieldProps("name")}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                margin="dense"
              />

              <Autocomplete
                options={organisations}
                getOptionLabel={(option) => option.name || ""}
                value={
                  typeof values.organisation === "object"
                    ? organisations.find(
                        (o) => o._id === values.organisation._id
                      ) || null
                    : organisations.find(
                        (o) => o._id === values.organisation
                      ) || null
                }
                onChange={(event, newValue) => {
                  // store ID in form
                  const newOrgId = newValue?._id || "";
                  setFieldValue("organisation", newOrgId);

                  // ensure subsequent ingredient searches are scoped to this org (esp. for System Admins)
                  setSearchOrganisationId(newOrgId);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organisation"
                    variant="outlined"
                    margin="dense"
                    error={touched.organisation && Boolean(errors.organisation)}
                    helperText={touched.organisation && errors.organisation}
                  />
                )}
              />

              {/* Weight and Total Percentage Display */}
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  type="number"
                  label="Weight (g)"
                  variant="outlined"
                  {...getFieldProps("weight")}
                  margin="dense"
                  error={touched.weight && Boolean(errors.weight)}
                  helperText={touched.weight && errors.weight}
                />
                <Box>
                  <Typography variant="body1">
                    Total Percentage: {totalPercentage}%
                  </Typography>
                  {typeof errors.ingredients === "string" && (
                    <Typography variant="body2" color="error">
                      {errors.ingredients}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Ingredients */}
              <Typography variant="h6" mt={2}>
                Ingredients
              </Typography>
              <FieldArray
                name="ingredients"
                render={(arrayHelpers) => (
                  <Box>
                    {values.ingredients.map((ingredient, index) => {
                      // For the Autocomplete we try to find a matching option
                      // from the search results using the ingredientId.
                      const selectedOption =
                        ingredientSearchResults.find(
                          (option) => option._id === ingredient.emissionRecordId
                        ) ||
                        ingredient.ingredientData ||
                        null;

                      return (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="center"
                          gap={2}
                          mt={1}
                        >
                          {/* Autocomplete for selecting ingredient using source_db_name */}
                          <Autocomplete
                            options={preparedOptions}
                            filterOptions={(opts, state) => filter(opts, state)}
                            groupBy={(opt) => {
                              const inActiveOrg =
                                String(opt.organisation) ===
                                String(activeOrgId);
                              const label = inActiveOrg
                                ? `${activeOrganisationName} specific ingredients`
                                : `Applicable Region: ${
                                    opt.applicable_region || "Other"
                                  }`;

                              // Prefix with sort key so the active org group stays on top.
                              // MUI sorts groups lexicographically; "0|" < "1|".
                              return `${inActiveOrg ? "0" : "1"}|${label}`;
                            }}
                            renderGroup={(params) => {
                              const [, cleanLabel] = params.group.split("|", 2);
                              return (
                                <React.Fragment key={params.group}>
                                  <ListSubheader
                                    component="div"
                                    sx={{
                                      backgroundColor: "background.paper",
                                      fontWeight: "bold",
                                      fontSize: "0.85rem",
                                      color: "text.primary",
                                    }}
                                  >
                                    {cleanLabel}
                                  </ListSubheader>
                                  {params.children}
                                </React.Fragment>
                              );
                            }}
                            getOptionLabel={(opt) => opt.source_product_name}
                            renderOption={(props, opt) => {
                              const farmGate =
                                opt.emissions?.cradle_to_farm_gate;
                              return (
                                <Box
                                  component="li"
                                  {...props}
                                  key={opt._id}
                                  sx={{
                                    py: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                    }}
                                  >
                                    {opt.source_product_name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Origin: {opt.origin_region} • Applicable:{" "}
                                    {opt.applicable_region}
                                    {farmGate != null && (
                                      <>
                                        {" "}
                                        • Farm-gate: {farmGate.toFixed(1)} kg
                                        CO₂e
                                      </>
                                    )}
                                  </Typography>
                                </Box>
                              );
                            }}
                            value={selectedOption}
                            onInputChange={(e, v) => setIngredientSearchTerm(v)}
                            onChange={(e, newVal) => {
                              setFieldValue(
                                `ingredients.${index}.emissionRecordId`,
                                newVal?._id || ""
                              );
                              setFieldValue(
                                `ingredients.${index}.ingredientData`,
                                newVal || null
                              );
                            }}
                            onBlur={() => setIngredientSearchTerm("")}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Emission Source"
                                variant="outlined"
                                margin="dense"
                                error={
                                  touched.ingredients &&
                                  touched.ingredients[index] &&
                                  touched.ingredients[index].emissionRecordId &&
                                  Boolean(
                                    errors.ingredients &&
                                      errors.ingredients[index] &&
                                      errors.ingredients[index].emissionRecordId
                                  )
                                }
                                helperText={
                                  touched.ingredients &&
                                  touched.ingredients[index] &&
                                  touched.ingredients[index].emissionRecordId &&
                                  errors.ingredients &&
                                  errors.ingredients[index] &&
                                  errors.ingredients[index].emissionRecordId
                                }
                              />
                            )}
                            sx={{ width: "75%" }}
                          />

                          <TextField
                            type="number"
                            label="% QUID"
                            variant="outlined"
                            {...getFieldProps(`ingredients.${index}.percent`)}
                            sx={{ width: "80px" }}
                            error={
                              touched.ingredients &&
                              touched.ingredients[index] &&
                              touched.ingredients[index].percent &&
                              Boolean(
                                errors.ingredients &&
                                  errors.ingredients[index] &&
                                  errors.ingredients[index].percent
                              )
                            }
                            helperText={
                              touched.ingredients &&
                              touched.ingredients[index] &&
                              touched.ingredients[index].percent &&
                              errors.ingredients &&
                              errors.ingredients[index] &&
                              errors.ingredients[index].percent
                            }
                            margin="dense"
                          />

                          <IconButton
                            onClick={() => arrayHelpers.remove(index)}
                            color="error"
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        </Box>
                      );
                    })}
                    <Button
                      startIcon={<AddCircleOutline />}
                      onClick={() =>
                        arrayHelpers.push({ emissionRecordId: "", percent: 0 })
                      }
                    >
                      Add Ingredient
                    </Button>
                  </Box>
                )}
              />

              <Box mt={2} gap={2} display="flex" justifyContent="space-between">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isValid || !dirty}
                >
                  {editMode ? "Update Product" : "Create Product"}
                </Button>
                {editMode && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteClick}
                  >
                    Delete Product
                  </Button>
                )}
              </Box>
              <DialogActions>
                <Button onClick={() => setOpen(false)} color="secondary">
                  Cancel
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
      <Dialog open={deleteProductDialogOpen} onClose={handleCloseDeleteDialog}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Are you sure you want to delete this product?
          </Typography>
          <Typography variant="body2" color="error">
            This action cannot be undone.
          </Typography>
        </Box>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProductForm;
